import { useContext, useEffect, useMemo, useState } from "react";
import { formJson } from "../../../utils/formJson";
import { useCommanKit } from "./useCommanKit";
import _ from "underscore";
import services from "../../../services/services";
import { Urls } from "../../../services/Urls";
import { ToastContext } from "../../../context/ToastContext";
import { convertToBase64, toLocalDateString } from "../../../utils/Helper";

export const useDetailsForm = ({ label, onNext, data }) => {
    const { showSuccess, showError, showInfo } = useContext(ToastContext);
    const { getEmployeeDetails } = useCommanKit({ data })
    const { fieldData, pageTitle, getDataKey, apiEndpoint } = formJson?.[label];

    const getInitialFormValues = () =>
        fieldData
            ? Object.fromEntries(
                Object.entries(fieldData).map(([section, fields]) => [
                    section,
                    Object.fromEntries(fields.inputs?.map(f => [f.name, ""]))
                ])
            )
            : {};
    const [formValues, setFormValues] = useState(getInitialFormValues());
    const [formErrors, setFormErrors] = useState({});
    const [isChanged, setIsChanged] = useState(false);


    const handleChange = async (section, e) => {
        setIsChanged(true)
        const { name, value } = e.target;
        const file = e.target?.files?.[0]
        if (file) {
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                showInfo("warn", "Only image and PDF files are allowed.");
                return;
            }
            if (file.size > 20 * 1024 * 1024) {
                showInfo("warn", "File size must be less than 20MB.");
                return;
            }
            const base64 = await convertToBase64(file)
            const url = URL.createObjectURL(file);
            setFormValues(prev => ({
                ...prev,
                [section]: {
                    ...prev[section], [name]: url,
                    [`${name.replace(/^driving_|aadhar_/, 'document_')}_id`]: base64?.split(",")?.[1]
                }
            }));
        } else {
            setFormValues(prev => ({
                ...prev,
                [section]: { ...prev[section], [name]: value }
            }));
        }
    };

    useEffect(() => {
        Object.entries(fieldData || {}).forEach(([sectionKey, sectionFields]) => {
            sectionFields.inputs?.forEach((field) => {
                if (field.type === "autocomplete" || field?.type === "select" && field?.optionType) {
                    const group = _.find(data?.attributeGroup, { attribute_group_key: field?.optionType });
                    if (group) {
                        const options = _.filter(data?.attributeItems, { attribute_group_id: group.attribute_group_id })
                            .map(item => ({
                                label: item.item_name,
                                value: field?.type === "select" ? item.attribute_item_id : item.item_name
                            }));
                        field.option = options;
                    } else {
                        field.option = [];
                    }
                }
            });
        });
    }, [fieldData, data?.attributeGroup, data?.attributeItems]);


    const validateForm = (fields, formValues) => {
        const errors = {};

        fields?.inputs?.forEach(field => {
            if (field.depand && !formValues[field.depand]) return;
            if (field.type === "button") return;
            if (field.required && !field?.disabled) {
                if (!formValues[field.name] || formValues[field.name].toString().trim() === "") {
                    errors[field.name] = `${field?.errorLabel ? "This field" : field.label} is required`;
                }
            }
            if (field.type === "number" && formValues[field.name]) {
                if (isNaN(formValues[field.name])) {
                    errors[field.name] = `${field.label} must be a number`;
                }
            }
            if (field.name?.toLowerCase().includes("email")) {
                if (formValues[field.name] && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formValues[field.name])) {
                    errors[field.name] = `Please enter valid ${field.label}`;
                }
            }
            if (field.name?.toLowerCase().includes("contact_number") || field.name?.toLowerCase().includes("mobile")) {
                if (formValues[field.name] && !/^\d{10}$/.test(formValues[field.name])) {
                    errors[field.name] = `${field.label} must be a valid 10-digit number`;
                }
            }
        });
        return errors;
    };

    function buildPayloadBySchema(schema, formValues) {
        const payload = {};

        for (const key in schema) {
            if (Object.hasOwn(formValues, key)) {
                let val = formValues[key];
                const schemaVal = schema[key];
                if (typeof schemaVal === "number") {
                    if (val instanceof Date) {
                        payload[key] = val.getFullYear();
                    } else {
                        payload[key] = Number(val) || 0;
                    }
                }
                else if (typeof schemaVal === "string") {
                    if (val instanceof Date) {
                        payload[key] = toLocalDateString(val);
                    } else {
                        payload[key] = val != null ? String(val) : "";
                    }
                }
                else if (typeof schemaVal === "boolean") {
                    payload[key] = val || schema[key];
                } else {
                    payload[key] = val;
                }
            } else {
                payload[key] = schema[key];
            }
        }
        return payload;
    }

    const handleNext = async (e) => {
        try {
            if (apiEndpoint) {
                e.preventDefault();
                let allErrors = {};
                let payload = {}
                Object.entries(fieldData).forEach(([section, fields]) => {
                    const sectionErrors = validateForm(fields, formValues[section] || {});
                    if (Object.keys(sectionErrors).length > 0) {
                        allErrors[section] = sectionErrors;
                        return
                    }

                    const sectionPayload = buildPayloadBySchema(fieldData[section]?.payload, formValues[section]);
                    payload = { ...payload, ...sectionPayload };
                });
                if (_.isEmpty(allErrors) && !isChanged) {
                    onNext();
                    return
                }
                setFormErrors(allErrors);
                if (Object.keys(allErrors).length === 0) {
                    const res = await services.getInvokeRemote("POST", Urls(apiEndpoint), payload)
                    if (!res?.body?.error_status) {
                        showSuccess("success", res?.body?.message);
                        onNext();
                    } else {
                        showError("error", res?.body?.errorMessage || res?.body?.message);
                    }
                }
            } else {
                onNext()
            }

        } catch (error) {
            console.error("error", error)
        }
    };

    useEffect(() => {
        data?.employee?.user_code && getEmployeeDetails(getDataKey, data?.employee?.user_code).then((res) => {

            const sectionUpdates = {};

            Object.entries(fieldData).forEach(([sectionName, sectionObj]) => {
                const data = res?.[sectionObj.key]?.[0] || {};
                const updatedData = { ...data };
                Object.keys(data).forEach((key) => {
                    if (key.includes('_file_id')) {
                        let newKey;
                        if (sectionName.includes('Aadhar')) {
                            if (key.includes('front')) newKey = 'aadhar_front_file';
                            else if (key.includes('back')) newKey = 'aadhar_back_file';
                        } else if (sectionName.includes('Driving')) {
                            if (key.includes('front')) newKey = 'driving_front_file';
                            else if (key.includes('back')) newKey = 'driving_back_file';
                        } else if (key.endsWith('_file_id')) {
                            newKey = key.replace(/_file_id$/, '_file');
                        }
                        if (newKey) {
                            updatedData[newKey] = data[key];
                            delete updatedData[key];
                        }
                    }
                });

                sectionUpdates[sectionName] = updatedData;
            });

            setFormValues((prev) => ({
                ...prev,
                ...sectionUpdates
            }));
        });
    }, [data?.employee]);


    const handleSave = async (e, section, key) => {
        try {
            e.preventDefault()
            const document_item_id = _.find(data?.attributeItems, { item_key: key })?.attribute_item_id
            let allErrors = {};
            const sectionErrors = validateForm(fieldData[section], formValues[section] || {});
            if (Object.keys(sectionErrors).length > 0) {
                allErrors[section] = sectionErrors;
            }
            setFormErrors(allErrors);
            if (Object.keys(allErrors).length === 0) {
                let payload
                const sectionPayload = buildPayloadBySchema(fieldData[section]?.payload, formValues[section]);
                payload = { ...payload, ...sectionPayload, document_item_id };
                const res = await services.getInvokeRemote("POST", Urls(fieldData[section]?.apiEndpoint), payload)
                if (!res?.body?.error_status) {
                    showSuccess("success", res?.body?.message);
                } else {
                    showError("error", res?.body?.errorMessage);
                }
            }
        } catch (error) {
            console.error("error")
        }
    }

    return useMemo(() => ({
        fieldData, pageTitle, formValues, handleChange, handleNext, formErrors, handleSave
    }), [fieldData, pageTitle, formValues, handleChange, handleNext, formErrors, handleSave])
}